import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, Compass, Sparkles, Flame, MessageCircle, Lightbulb, Route, ShieldCheck, IndianRupee } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/roles";
import { useSite, DEFAULT_LOGO } from "@/lib/site";
import Navbar from "@/components/Navbar";
import { SiteFooter } from "@/components/Brand";
import { MentorCard } from "@/components/MentorCard";
import { Stars, ReviewAvatar } from "@/components/Reviews";
import { Button } from "@/components/ui/button";

const CREAM = "#F5F1E8";
const U = (id, w = 1200) => `https://images.unsplash.com/photo-${id}?w=${w}&q=75&auto=format&fit=crop`;
const IMG = {
  hero: U("1600880292203-757bb62b4baf", 1400),
  mentors: U("1531482615713-2afd69097998", 900),
  assessments: U("1543269865-cbf427effbad", 900),
  story: U("1522202176988-66273c2fd55f", 1400),
  cta: U("1552664730-d307ca884978", 1600),
};

const PathCard = ({ to, icon: Icon, eyebrow, title, desc, cta, dark, testId, delay, image, alt }) => {
  const navigate = useNavigate();
  return (
    <motion.button initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay, duration: 0.5 }}
      onClick={() => navigate(to)} data-testid={testId}
      className={`group text-left rounded-[2rem] overflow-hidden border transition-all hover:-translate-y-1 hover:shadow-2xl ${dark ? "bg-primary border-primary text-white shadow-xl shadow-primary/30" : "bg-white border-slate-200 text-slate-900 shadow-lg"}`}>
      <div className="relative h-56 sm:h-64 overflow-hidden">
        <img src={image} alt={alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className={`absolute inset-0 ${dark ? "bg-gradient-to-t from-primary via-primary/30 to-transparent" : "bg-gradient-to-t from-white via-white/20 to-transparent"}`} />
        <div className={`absolute left-8 sm:left-10 bottom-4 w-14 h-14 rounded-2xl flex items-center justify-center backdrop-blur-md ${dark ? "bg-white/15" : "bg-white/80 shadow-md"}`}>
          <Icon className={`w-7 h-7 ${dark ? "text-white" : "text-primary"}`} />
        </div>
      </div>
      <div className="p-8 sm:p-10 pt-4 sm:pt-5">
      <p className={`font-body text-xs uppercase tracking-[0.2em] ${dark ? "text-sky-200" : "text-primary"}`}>{eyebrow}</p>
      <h3 className="font-heading font-extrabold text-2xl sm:text-3xl mt-2 leading-tight">{title}</h3>
      <p className={`font-body text-base mt-4 leading-relaxed ${dark ? "text-white/80" : "text-slate-600"}`}>{desc}</p>
      <span className={`inline-flex items-center gap-2 mt-8 font-heading font-bold ${dark ? "text-sky-200" : "text-primary"}`}>
        {cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
      </span>
      </div>
    </motion.button>
  );
};

const steps = [
  { icon: Lightbulb, t: "Pick your path", d: "Browse mentors by expertise or choose the assessment track that fits your stage." },
  { icon: IndianRupee, t: "Pay simply via UPI", d: "Scan, pay, share your transaction ID. No cards, no sign-ups, no subscriptions you forget." },
  { icon: MessageCircle, t: "Get connected", d: "We verify and introduce you to your mentor — or email your assessment access — within hours." },
  { icon: Route, t: "Grow with direction", d: "Regular conversations, honest feedback and a report you can act on. That's the 10x." },
];

const Landing = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const site = useSite();
  const [mentors, setMentors] = useState([]);
  const [stories, setStories] = useState([]);

  useEffect(() => { if (!loading && user) navigate(isAdminRole(user) ? "/admin" : "/dashboard"); }, [user, loading, navigate]);
  useEffect(() => { api.get("/mentors").then((r) => setMentors(r.data.mentors.slice(0, 3))).catch(() => {}); api.get("/stories").then((r) => setStories(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B2F55]" data-testid="home-hero">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(#F5F1E8 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-primary/40 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#F5F1E8]/20 bg-white/5 mb-8">
              <Flame className="w-4 h-4 text-amber-300" />
              <span className="font-body text-sm text-[#F5F1E8]/80">Mentoring · Career assessments · Real conversations</span>
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight" style={{ color: CREAM }} data-testid="hero-title">
              Lighting up lives<br />with mentoring.
              <span className="block text-sky-300 mt-2">10x your growth.</span>
            </h1>
            <p className="font-body text-base md:text-lg mt-8 leading-relaxed max-w-2xl" style={{ color: "rgba(245,241,232,0.78)" }} data-testid="hero-copy">
              A mentor is a brain to pick, ears to listen and someone who pushes you in the right direction. If you are looking at guidance, learning something new and are searching for someone who can shine light on your questions, then this is the place to be! <span className="font-semibold" style={{ color: CREAM }}>Connect to our mentors now!</span>
            </p>
            <p className="font-body text-base md:text-lg mt-4 leading-relaxed max-w-2xl" style={{ color: "rgba(245,241,232,0.78)" }}>
              And to give you that extra edge, our AI-enabled assessments will help you whether you are starting your career or trying to make a comeback after a break.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Button onClick={() => navigate("/mentors")} data-testid="hero-mentors-btn"
                className="rounded-full h-13 px-8 py-3.5 text-base font-heading font-bold bg-[#F5F1E8] text-[#0B2F55] hover:bg-white transition-transform hover:scale-[1.02] active:scale-[0.98]">
                <Users className="w-5 h-5 mr-2" /> Connect to a mentor
              </Button>
              <Button onClick={() => navigate("/assessments")} variant="outline" data-testid="hero-assessments-btn"
                className="rounded-full h-13 px-8 py-3.5 text-base font-heading font-bold border-[#F5F1E8]/30 bg-transparent text-[#F5F1E8] hover:bg-white/10 hover:text-white transition-transform hover:scale-[1.02]">
                <Compass className="w-5 h-5 mr-2" /> Career assessments
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
            <div className="absolute -inset-6 rounded-[3rem] bg-sky-400/20 blur-3xl" />
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl ring-1 ring-white/20 aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]">
              <img src={IMG.hero} alt="Mentor and mentee celebrating a win" data-testid="hero-photo" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B2F55]/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
                <div className="bg-[#F5F1E8]/95 backdrop-blur rounded-2xl px-4 py-3 flex items-center gap-3 shadow-xl">
                  <ShieldCheck className="w-5 h-5 text-primary shrink-0" />
                  <div>
                    <p className="font-heading font-bold text-sm text-slate-900">Hand-picked mentors</p>
                    <p className="font-body text-xs text-slate-500">Every profile reviewed by our team</p>
                  </div>
                </div>
                <img src={site.logo || DEFAULT_LOGO} alt="Motley Mentors" data-testid="hero-logo" className="w-16 h-16 rounded-2xl object-cover shadow-xl ring-2 ring-white/40 shrink-0" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TWO PATHS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 -mt-10 relative z-10 pb-20" data-testid="paths-section">
        <div className="grid md:grid-cols-2 gap-6">
          <PathCard to="/mentors" icon={Users} eyebrow="Connect to a mentor" title="Talk to someone who's been there." delay={0} testId="path-mentors" image={IMG.mentors} alt="Mentor guiding a mentee at a screen"
            desc="Founders, leaders, specialists and career-changers who've walked the road you're on. Book a single session or a monthly mentorship and get honest, practical direction." cta="Browse mentors" />
          <PathCard to="/assessments" icon={Compass} eyebrow="Career assessments" title="Know yourself. Map your move." dark delay={0.1} testId="path-assessments" image={IMG.assessments} alt="Young people exploring results together"
            desc="CareerCompass blends cognitive ability, aptitude, DISC personality and Holland interests into an AI-written career alignment report — with a Start track for students and a Comeback track for returners and changers." cta="Explore assessments" />
        </div>
      </section>

      {/* MENTORS PREVIEW */}
      {mentors.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20" data-testid="featured-mentors">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <p className="font-body text-xs uppercase tracking-[0.2em] text-primary">Meet the mentors</p>
              <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900 mt-2">People who light the way</h2>
            </div>
            <Link to="/mentors" className="font-heading font-bold text-primary inline-flex items-center gap-1.5 hover:gap-2.5 transition-all" data-testid="see-all-mentors-link">See all mentors <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {mentors.map((m, i) => <MentorCard key={m.mentor_id} mentor={m} index={i} currency={site.currency} />)}
          </div>
        </section>
      )}

      {/* SUCCESS STORIES */}
      {stories.length > 0 && (
        <section className="bg-[#0B2F55] py-20 overflow-hidden" data-testid="success-stories">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-body text-xs uppercase tracking-[0.2em] text-sky-300">Success stories</p>
                <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#F5F1E8] mt-2">Real people. Real outcomes.</h2>
              </div>
              <p className="font-body text-sm text-[#F5F1E8]/60 max-w-sm">Every story below started with one conversation. Yours can too.</p>
            </div>
            <div className="mt-10 flex gap-5 overflow-x-auto pb-4 -mx-5 px-5 sm:mx-0 sm:px-0 snap-x" style={{ scrollbarWidth: "none" }}>
              {stories.map((s, i) => (
                <motion.article key={s.review_id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="snap-start shrink-0 w-[320px] sm:w-[360px] bg-[#F5F1E8] rounded-3xl p-6 flex flex-col" data-testid={`story-${s.review_id}`}>
                  <div className="flex items-center gap-3">
                    <ReviewAvatar r={s} className="w-14 h-14" />
                    <div className="min-w-0">
                      <p className="font-heading font-bold text-slate-900 truncate">{s.name}</p>
                      <p className="font-body text-xs text-slate-500 truncate">{s.role}</p>
                    </div>
                  </div>
                  {s.outcome && <p className="mt-4 inline-flex self-start font-heading font-bold text-xs text-primary bg-primary/10 rounded-full px-3 py-1">{s.outcome}</p>}
                  <p className="font-body text-sm text-slate-700 leading-relaxed mt-4 flex-1">“{s.text}”</p>
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-900/10">
                    <Stars rating={s.rating} />
                    <Link to={`/mentors/${s.mentor_slug}`} className="font-body text-xs text-slate-500 hover:text-primary">with {s.mentor_name.split(" ")[0]} →</Link>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* STORY BAND */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20" data-testid="story-section">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-8 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
            <img src={IMG.story} alt="Peers learning together with a mentor" loading="lazy" className="rounded-[2rem] w-full aspect-[4/3] object-cover shadow-xl" />
            <div className="absolute -bottom-6 -right-4 sm:right-8 bg-primary text-white rounded-2xl px-5 py-4 shadow-xl">
              <p className="font-heading font-extrabold text-3xl leading-none">10x</p>
              <p className="font-body text-xs text-sky-100 mt-1">growth mindset, guided</p>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:pl-6">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-primary">Why mentoring</p>
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900 mt-2 leading-tight">A brain to pick, ears to listen, and a push in the right direction.</h2>
            <p className="font-body text-base text-slate-600 mt-5 leading-relaxed">Courses teach you the what. Mentors help you with the how, the when and the "is this really for me?". Our mentors have switched careers, restarted after breaks, built teams and hired freshers — and they'll tell you what they wish someone had told them.</p>
            <ul className="mt-6 space-y-3">
              {["Honest, personalised feedback — not generic advice", "Single sessions or monthly plans, your pace", "Pair it with a CareerCompass report for a data-backed conversation"].map((t) => (
                <li key={t} className="font-body text-sm text-slate-700 flex items-start gap-2.5"><Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {t}</li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-primary">How it works</p>
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900 mt-2 max-w-xl">Simple, human and built around you</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {steps.map((s, i) => (
              <motion.div key={s.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} data-testid={`step-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-heading font-extrabold text-4xl text-slate-200">0{i + 1}</span>
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><s.icon className="w-5 h-5 text-primary" /></div>
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900">{s.t}</h3>
                <p className="font-body text-sm text-slate-500 mt-2 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="rounded-[2rem] relative overflow-hidden p-10 sm:p-14 grid md:grid-cols-[1.3fr_1fr] gap-8 items-center">
          <img src={IMG.cta} alt="Mentor leading a workshop" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B2F55]/85" />
          <div className="relative z-10">
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-white">Want to give back? Mentor with us.</h2>
            <p className="font-body text-base text-sky-100 mt-4 max-w-xl">Share what you've learned, set your own session and monthly pricing, and help someone find their footing. Applications are reviewed personally.</p>
            <Button onClick={() => navigate("/contact?interest=mentor")} data-testid="cta-become-mentor-btn"
              className="mt-8 rounded-full h-12 px-7 bg-white text-primary hover:bg-sky-50 font-heading font-bold transition-transform hover:scale-[1.02]">
              Apply to become a mentor <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <Sparkles className="absolute -right-10 -bottom-10 w-72 h-72 text-white/10 z-10" />
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Landing;
